/**
 * AI Provider configurations for authentication and API access
 * Based on IMPLEMENTATION_PLAN.md Section 5
 */

import type { ProviderConfig } from "@/lib/ai/types.ts";

export const PROVIDERS = {
  default: {
    id: "default",
    name: "Default",
    authType: "none",
    apiUrl: "",
    defaultModel: "auto",
    availableModels: [],
  },

  copilot: {
    id: "copilot",
    name: "GitHub Copilot",
    authType: "device_code",
    clientId: "Iv23liVz7cIsVZUNqAme",
    tokenUrl: "https://github.com/login/oauth/access_token",
    apiUrl: "https://api.githubcopilot.com/chat/completions",
    defaultModel: "gpt-4o-mini",
    availableModels: [
      "gpt-4o",
      "gpt-4o-mini",
      "o1-preview",
      "o1-mini",
      "claude-3.7-sonnet",
    ],
  },

  antigravity: {
    id: "antigravity",
    name: "Google Antigravity",
    authType: "oauth_pkce",
    clientId: "1071006060591-tmhssin2h21lcre235vtolojh4g403ep.apps.googleusercontent.com",
    authUrl: "https://accounts.google.com/o/oauth2/v2/auth",
    tokenUrl: "https://oauth2.googleapis.com/token",
    apiUrl: "https://cloudcode-pa.googleapis.com", // Production
    // apiUrl: "https://daily-cloudcode-pa.sandbox.googleapis.com", // Sandbox
    scopes: [
      "openid",
      "email",
      "profile",
      "https://www.googleapis.com/auth/cloud-platform",
    ],
    defaultModel: "claude-sonnet-4-5",
    availableModels: [
      "claude-sonnet-4-5",
      "gemini-2.0-flash-thinking-exp",
      "gemini-exp-1206",
    ],
  },

  openrouter: {
    id: "openrouter",
    name: "OpenRouter",
    authType: "api_key",
    apiUrl: "https://openrouter.ai/api/v1",
    defaultModel: "anthropic/claude-3.5-sonnet",
    availableModels: [],
  },

  gemini: {
    id: "gemini",
    name: "Google Gemini",
    authType: "api_key",
    apiUrl: "https://generativelanguage.googleapis.com/v1beta",
    defaultModel: "gemini-2.0-flash",
    availableModels: [
      "gemini-2.0-flash",
      "gemini-2.5-flash-preview-05-20",
      "gemini-2.5-pro-preview-05-06",
      "gemini-1.5-pro",
      "gemini-1.5-flash",
    ],
  },

  groq: {
    id: "groq",
    name: "Groq",
    authType: "api_key",
    apiUrl: "https://api.groq.com/openai/v1",
    defaultModel: "llama-3.3-70b-versatile",
    availableModels: [
      "llama-3.3-70b-versatile",
      "llama-3.1-8b-instant",
      "gemma2-9b-it",
      "mixtral-8x7b-32768",
    ],
  },

  perplexity: {
    id: "perplexity",
    name: "Perplexity",
    authType: "api_key",
    apiUrl: "https://api.perplexity.ai",
    defaultModel: "sonar",
    availableModels: [
      "sonar",
      "sonar-pro",
      "sonar-reasoning",
      "sonar-reasoning-pro",
    ],
  },
} satisfies Record<string, ProviderConfig>;

export const REDIRECT_URI = `${globalThis.location.origin}/auth/callback`;

/**
 * Get provider config by ID
 */
export function getProviderConfig(
  providerId: string,
): ProviderConfig | undefined {
  return (PROVIDERS as Record<string, ProviderConfig>)[providerId];
}

/**
 * Get all available providers as array
 */
export function getAllProviders(): ProviderConfig[] {
  return Object.values(PROVIDERS);
}
